import { generateId } from "./id";

// Modelo de dados para marcação de avarias/riscos externos do veículo.
// Suporta dois métodos de marcação, que podem ser usados em conjunto:
//   - diagrama: registros de avaria vinculados a uma peça específica da
//     carroceria (modelo 3D interativo, rotação livre 360°)
//   - fotos: pontos marcados sobre fotos reais do veículo (câmera ou upload),
//     até MAX_FOTOS_AVARIA fotos

// Limite de fotos para não sobrecarregar o armazenamento local/sistema.
export const MAX_FOTOS_AVARIA = 6;

// Limite de fotos por registro de avaria no modelo 3D.
export const MAX_FOTOS_POR_AVARIA_PECA = 4;

// Ângulos de câmera predefinidos para o modelo 3D — clicáveis como botões de
// "vista rápida", mas sem impedir a rotação livre com o mouse/touch a
// qualquer momento (ver VehicleInspection3D.jsx).
export const VISTAS_VEICULO = [
  { key: "frontal", label: "Frontal" },
  { key: "traseira", label: "Traseira" },
  { key: "lateral_esquerda", label: "Lateral esquerda" },
  { key: "lateral_direita", label: "Lateral direita" },
  { key: "superior", label: "Superior" },
];

// Catálogo de peças da carroceria, mapeado para os nós nomeados do modelo 3D
// (ver vehicleParts3d.js para o mapeamento nó-do-modelo → peça). Cada peça é
// uma região independente e clicável no modelo 3D, com hover, seleção e
// destaque visual (cor por gravidade) próprios.
export const PARTES_VEICULO = [
  { key: "hood", label: "Capô" },
  { key: "headlights", label: "Faróis" },
  { key: "windshield", label: "Para-brisa" },
  { key: "roof", label: "Teto" },
  { key: "left_mirror", label: "Retrovisor esquerdo" },
  { key: "right_mirror", label: "Retrovisor direito" },
  { key: "left_door", label: "Porta esquerda" },
  { key: "right_door", label: "Porta direita" },
  { key: "rear_quarter_glass", label: "Vidro lateral traseiro" },
  { key: "rear_window", label: "Vidro traseiro" },
  { key: "trunk", label: "Tampa traseira / porta-malas" },
  { key: "taillights", label: "Lanternas traseiras" },
  { key: "rear_panel", label: "Painel traseiro" },
  { key: "side_panel", label: "Saia lateral / painel inferior" },
  { key: "license_plate", label: "Placa" },
  { key: "wheel_front_left", label: "Roda dianteira esquerda" },
  { key: "wheel_front_right", label: "Roda dianteira direita" },
  { key: "wheel_rear_left", label: "Roda traseira esquerda" },
  { key: "wheel_rear_right", label: "Roda traseira direita" },
  // Fase 3 (validação de arquitetura com novos modelos): peças adicionais,
  // aditivas -- o modelo padrão (car-concept) nunca mapeia nó nenhum pra
  // essas chaves, então isso não muda nada do que já funciona. Existem
  // porque alguns modelos candidatos não separam capô/porta como o padrão,
  // só carroceria única + para-choques.
  { key: "body", label: "Carroceria" },
  { key: "front_bumper", label: "Para-choque dianteiro" },
  { key: "rear_bumper", label: "Para-choque traseiro" },
];

export function getParteLabel(key) {
  return PARTES_VEICULO.find((p) => p.key === key)?.label || key;
}

export const TIPOS_AVARIA = [
  "Risco",
  "Amassado",
  "Trinca",
  "Quebrado",
  "Peça faltante",
  "Repintura",
  "Corrosão",
  "Outro",
];

export const GRAVIDADE_AVARIA = {
  LEVE: "leve",
  MODERADA: "moderada",
  CRITICA: "critica",
};

export const GRAVIDADE_LABELS = {
  [GRAVIDADE_AVARIA.LEVE]: "Leve",
  [GRAVIDADE_AVARIA.MODERADA]: "Moderada",
  [GRAVIDADE_AVARIA.CRITICA]: "Crítica",
};

export const GRAVIDADE_COLORS = {
  [GRAVIDADE_AVARIA.LEVE]: "#d97706",
  [GRAVIDADE_AVARIA.MODERADA]: "#ea580c",
  [GRAVIDADE_AVARIA.CRITICA]: "#dc2626",
};

// ── MARCAÇÕES SOBRE A FOTO ────────────────────────────────────────────────
// Uma marcação sobre foto sempre teve coordenada em porcentagem (x, y) e
// classificação (tipo / gravidade / observação). A partir daqui ela também
// tem FORMA, o que permite seta, retângulo, elipse e texto além do pino.
//
// COMPATIBILIDADE: registro salvo antes disso não tem o campo "forma".
// normalizarPonto() preenche como pino, então checklist antigo abre
// exatamente como abria -- nenhuma migração, nenhum dado reescrito, e
// countAvariasCriticas/resumoSaudeVeiculo continuam lendo p.gravidade como
// sempre leram, valha a marcação qual forma for.
export const FORMAS = {
  PIN: "pin",
  SETA: "seta",
  RETANGULO: "retangulo",
  ELIPSE: "elipse",
  TEXTO: "texto",
};

// Formas que precisam de um segundo ponto (x2/y2), obtido por arrasto.
export const FORMAS_COM_ARRASTE = [FORMAS.SETA, FORMAS.RETANGULO, FORMAS.ELIPSE];

// Paleta de alto contraste sobre lataria. O primeiro item (valor null)
// preserva o comportamento antigo: a cor sai da gravidade, automaticamente.
export const CORES_MARCACAO = [
  { valor: null, rotulo: "Automática (pela gravidade)" },
  { valor: "#dc2626", rotulo: "Vermelho" },
  { valor: "#ea580c", rotulo: "Laranja" },
  { valor: "#facc15", rotulo: "Amarelo" },
  { valor: "#16a34a", rotulo: "Verde" },
  { valor: "#2563eb", rotulo: "Azul" },
  { valor: "#db2777", rotulo: "Magenta" },
  { valor: "#ffffff", rotulo: "Branco" },
  { valor: "#111827", rotulo: "Preto" },
];

export const ESPESSURAS = [
  { valor: 2, rotulo: "Fina" },
  { valor: 4, rotulo: "Média" },
  { valor: 7, rotulo: "Grossa" },
];

// Cor efetiva de uma marcação: a escolhida à mão vence; sem escolha, cai na
// cor da gravidade -- que é como sempre funcionou.
export function corDaMarcacao(p) {
  return (p && p.cor) || GRAVIDADE_COLORS[p && p.gravidade] || "#6b7280";
}

// Completa os campos que marcações antigas não têm, sem alterar o que já
// existe. Função pura: devolve um objeto novo, não toca no recebido.
export function normalizarPonto(p) {
  const base = p || {};
  return {
    ...base,
    forma: base.forma || FORMAS.PIN,
    cor: base.cor || null,
    espessura: typeof base.espessura === "number" ? base.espessura : 4,
    texto: base.texto || "",
    x2: typeof base.x2 === "number" ? base.x2 : null,
    y2: typeof base.y2 === "number" ? base.y2 : null,
  };
}

// Cria um novo registro de avaria vinculado a uma peça do diagrama,
// já com o histórico inicial preenchido.
export function criarRegistroAvariaPeca({ parteId, tipo, gravidade, comentario, fotos, pontoLocal }) {
  const agora = new Date().toISOString();
  return {
    id: generateId("avp"),
    parteId,
    tipo: tipo || TIPOS_AVARIA[0],
    gravidade: gravidade || GRAVIDADE_AVARIA.LEVE,
    comentario: comentario || "",
    fotos: Array.isArray(fotos) ? fotos : [],
    // Ponto exato do clique (raycast) no modelo 3D, em coordenadas locais da
    // cena -- opcional: registros criados antes desse campo existir (ou sem
    // ponto capturado por algum motivo) simplesmente não desenham o marcador
    // premium, mas continuam funcionando normalmente (destaque da peça inteira
    // já existente permanece intacto).
    pontoLocal: pontoLocal && typeof pontoLocal.x === "number" ? pontoLocal : null,
    criadoEm: agora,
    atualizadoEm: agora,
    historico: [{ ts: agora, acao: "Avaria registrada" }],
  };
}

export function createEmptyAvarias() {
  return { diagrama: [], fotos: [] };
}

// Garante que um checklist antigo (criado antes deste recurso existir) tenha
// a estrutura de avarias presente, sem quebrar dados já salvos. Registros do
// diagrama no formato antigo (pontos x/y por ângulo) não são compatíveis com
// o novo modelo por peça e são descartados — somente as fotos são
// preservadas integralmente.
export function normalizeAvarias(avarias) {
  if (!avarias) return createEmptyAvarias();
  const diagramaBruto = Array.isArray(avarias.diagrama) ? avarias.diagrama : [];
  const diagrama = diagramaBruto.filter((registro) => registro && typeof registro.parteId === "string");
  return {
    diagrama,
    fotos: Array.isArray(avarias.fotos) ? avarias.fotos : [],
  };
}

// Conta avarias de gravidade crítica — usado como critério de avaliação
// (Dashboard, Relatórios e export PDF/HTML tratam isso como item crítico).
export function countAvariasCriticas(avarias) {
  const a = normalizeAvarias(avarias);
  const noDiagrama = a.diagrama.filter((r) => r.gravidade === GRAVIDADE_AVARIA.CRITICA).length;
  const nasFotos = a.fotos.reduce(
    (total, foto) => total + foto.pontos.filter((p) => p.gravidade === GRAVIDADE_AVARIA.CRITICA).length,
    0
  );
  return noDiagrama + nasFotos;
}

export function countAvariasTotal(avarias) {
  const a = normalizeAvarias(avarias);
  const nasFotos = a.fotos.reduce((total, foto) => total + foto.pontos.length, 0);
  return a.diagrama.length + nasFotos;
}

// Fase 6 (evolução do módulo 3D, combinada com o usuário): indicador
// automático de saúde do veículo. Reaproveita as mesmas contagens de
// countAvariasCriticas/countAvariasTotal (não as substitui, ambas continuam
// existindo e sendo usadas onde já eram) -- só resume tudo num status único
// para exibição em destaque (badge/card), no lugar do texto simples
// "N crítica(s)" que existia antes.
export const STATUS_SAUDE = { OK: "ok", ATENCAO: "atencao", CRITICO: "critico" };

export const STATUS_SAUDE_LABELS = {
  [STATUS_SAUDE.OK]: "OK",
  [STATUS_SAUDE.ATENCAO]: "Atenção",
  [STATUS_SAUDE.CRITICO]: "Crítico",
};

export const STATUS_SAUDE_COLORS = {
  [STATUS_SAUDE.OK]: "#16a34a",
  [STATUS_SAUDE.ATENCAO]: "#d97706",
  [STATUS_SAUDE.CRITICO]: "#dc2626",
};

export function resumoSaudeVeiculo(avarias) {
  const a = normalizeAvarias(avarias);
  const contagem = { leve: 0, moderada: 0, critica: 0 };
  for (const r of a.diagrama) contagem[r.gravidade] = (contagem[r.gravidade] || 0) + 1;
  for (const foto of a.fotos) {
    for (const p of foto.pontos) contagem[p.gravidade] = (contagem[p.gravidade] || 0) + 1;
  }
  const total = contagem.leve + contagem.moderada + contagem.critica;
  let status = STATUS_SAUDE.OK;
  if (contagem.critica > 0) status = STATUS_SAUDE.CRITICO;
  else if (contagem.moderada > 0 || contagem.leve > 0) status = STATUS_SAUDE.ATENCAO;
  return { status, total, contagem };
}
